import { Button, ConfigProvider, message, Modal } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { DataTable, FilterSearchPayload } from "@/app/home/components/table/DataTable";
import { MakeMultiElectronicContractModal } from "./MakeMultiElectronicContractModal";
import {
    FilterInputTemplate,
    FilterInputType,
    handleAddressChange,
    FilterBar,
} from "@/app/home/components/filter/FilterBar";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { AddressRepo } from "@/core/repos/address.repo";
import { WaterUserDepositStatus, WaterUserStatus } from "@/core/enums/global.enums";
import { contractStatusPipe } from "@/core/pipes/contract-status.pipe";
import { depositStatusPipe } from "@/core/pipes/deposit-status.pipe";

const contractRepo = container.get(ContractRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const unitTypeRepo = container.get(UnitTypeRepo);
const addressRepo = container.get(AddressRepo);

interface CreateElectronicContractMultiModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    query?: any,
}

export const CreateElectronicContractMultiModal: React.FC<CreateElectronicContractMultiModalProps> = ({isModalOpen, setIsModalOpen, query}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [isNextModal, setNextModal] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<ContractModel[]>([]);
    const [allUserIds, setAllUserIds] = useState<string[]>([]);
    const [isSelectAllPages, setIsSelectAllPages] = useState(false);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(
        new Map()
    );
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>(
        [
            {
                dataIndex: "search",
                label: "Tên/ Mã hợp đồng",
                placeholder: "Nhập Tên/ Mã hợp đồng",
                type: FilterInputType.text,
            },
            {
                dataIndex: "note",
                label: "Ghi chú",
                placeholder: "Nhập ghi chú",
                type: FilterInputType.text,
            },
            {
                dataIndex: "meterCode",
                label: "Mã đồng hồ",
                placeholder: "Nhập mã đồng hồ",
                type: FilterInputType.text,
            },
            {
                dataIndex: "phone",
                label: "Số điện thoại",
                placeholder: "Nhập số điện thoại",
                type: FilterInputType.text,
            },
            {
                dataIndex: "identityNo",
                label: "CCCD",
                placeholder: "Nhập CCCD",
                type: FilterInputType.text,
            },
            {
                dataIndex: "hasMeter",
                label: "Trạng thái lắp đồng hồ",
                placeholder: "Chọn Trạng thái lắp đồng hồ",
                type: FilterInputType.select,
                options: [
                    {
                        name: "Có đồng hồ",
                        value: true,
                    },
                    {
                        name: "Chưa có đồng hồ",
                        value: false,
                    },
                ],
            },
            {
                dataIndex: "status",
                label: "Trạng thái hoạt động",
                placeholder: "Chọn Trạng thái hoạt động",
                type: FilterInputType.select,
                options: [
                    {
                        name: contractStatusPipe(WaterUserStatus.Processing),
                        value: WaterUserStatus.Processing,
                    },
                    {
                        name: contractStatusPipe(WaterUserStatus.Active),
                        value: WaterUserStatus.Active,
                    },
                    {
                        name: contractStatusPipe(WaterUserStatus.TempDisabled),
                        value: WaterUserStatus.TempDisabled,
                    },
                    {
                        name: contractStatusPipe(WaterUserStatus.Disabled),
                        value: WaterUserStatus.Disabled,
                    },
                ],
            },
            {
                dataIndex: "tollAreaId",
                label: "Khu vực",
                placeholder: "Chọn Khu vực",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "unitTypeId",
                label: "Loại hình đơn vị",
                placeholder: "Chọn Loại hình đơn vị",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "provinceId",
                label: "Tỉnh/ TP",
                placeholder: "Chọn Tỉnh/ TP",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "districtId",
                label: "Quận/ Huyện",
                placeholder: "Chọn Quận/ Huyện",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "wardId",
                label: "Xã/ Phường",
                placeholder: "Chọn Xã/ Phường",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "depositStatus",
                label: "Trạng thái thu cọc",
                placeholder: "Chọn trạng thái thu cọc",
                type: FilterInputType.select,
                options: Object.keys(WaterUserDepositStatus).map((key) => ({name: depositStatusPipe(key), value: key})),
            },
        ]
    );

    useEffect(() => {
        if (isModalOpen) {
            init();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const populateSelections = async () => {
        const provinces = (await addressRepo.getAllProvinces()).map((data) => ({
            name: data[1].name,
            value: data[0],
        }));
        const tollAreas = (await tollAreaRepo.getAll()).map((area) => ({
            name: area.name,
            value: area.id,
        }));
        const unitTypes = (await unitTypeRepo.getAll()).map((unitType) => ({
            name: unitType.name,
            value: unitType.id,
        }));
        const templates: FilterInputTemplate[] = [...filterTemplates];
        const provinceTemplate = templates.find(
            (template) => template.dataIndex === "provinceId"
        );
        if (provinceTemplate) {
            provinceTemplate.options = provinces;
        }
        const tollAreaTemplate = templates.find(
            (template) => template.dataIndex === "tollAreaId"
        );
        if (tollAreaTemplate) {
            tollAreaTemplate.options = tollAreas;
        }
        const unitTypeTemplate = templates.find(
            (template) => template.dataIndex === "unitTypeId"
        );
        if (unitTypeTemplate) {
            unitTypeTemplate.options = unitTypes;
        }
        setFilterTemplates(templates);
    };

    const init = async () => {
        await populateSelections();
        if (query) {
            const newFilterMap = new Map<string, FilterSearchPayload>();
            Object.keys(query).forEach(key => {
                if (query[key] !== undefined && query[key] !== null && query[key] !== "") {
                    newFilterMap.set(key, { key, value: query[key] });
                }
            });
            setFilterMap(newFilterMap);
        }
    };

    async function onFilterChange(
        map: Map<string, FilterSearchPayload>,
        changes: string[]
    ) {
        await handleAddressChange(
            map,
            changes,
            filterTemplates,
            setFilterTemplates
        );
        setFilterMap(new Map(map));
    }

    const handleSelectAllPages = async () => {
        try {
            // Build query params from filterMap
            const queryParams: any = {};
            filterMap.forEach(filter => {
                if (filter.value !== undefined && filter.value !== null && filter.value !== "") {
                    queryParams[filter.key] = filter.value;
                }
            });

            const ids = await contractRepo.getAllIds(queryParams);
            setAllUserIds(ids);
            setIsSelectAllPages(true);
            messageApi.success(`Đã chọn tất cả ${ids.length} khách hàng từ tất cả các trang`);
        } catch (error) {
            messageApi.error("Có lỗi xảy ra khi tải danh sách khách hàng");
        }
    };

    function onRowSelect(rows: ContractModel[]) {
        setSelectedRows([...rows]);
        
        // Check if this is a "select all" action (when all rows on current page are selected)
        if (rows.length === currentPageSize && currentPageSize > 0) {
            // This means user clicked "select all" on current page
            // We want to select all pages instead
            handleSelectAllPages();
        } else if (rows.length === 0) {
            // User unselected all
            setIsSelectAllPages(false);
            setAllUserIds([]);
        } else if (rows.length < currentPageSize && isSelectAllPages) {
            // User manually selected some rows, disable select all pages mode
            setIsSelectAllPages(false);
            setAllUserIds([]);
        }
    }

    const onPagination = (current: number, pageSize: number) => {
        setCurrentPageSize(pageSize);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const selectedCount = isSelectAllPages ? allUserIds.length : selectedRows.length;
            if (selectedCount === 0) {
                message.error("Vui lòng chọn ít nhất một khách hàng");
                setLoading(false);
                return;
            }

            // If select all pages, we need to fetch the actual contract data
            let contractsToProcess: ContractModel[] = [];
            if (isSelectAllPages) {
                // Build query params from filterMap
                const queryParams: any = {};
                filterMap.forEach(filter => {
                    if (filter.value !== undefined && filter.value !== null && filter.value !== "") {
                        queryParams[filter.key] = filter.value;
                    }
                });
                // Fetch all contracts by IDs - get all in one call
                const allData = await contractRepo.getData(1, queryParams, allUserIds.length);
                if (allData && allData.data) {
                    // Filter to only include selected IDs
                    contractsToProcess = allData.data.filter(contract => allUserIds.includes(contract.id));
                }
            } else {
                contractsToProcess = selectedRows;
            }

            if (contractsToProcess.length === 0) {
                message.error("Không tìm thấy khách hàng để tạo hợp đồng");
                setLoading(false);
                return;
            }

            // Store contracts for next modal
            setSelectedRows(contractsToProcess);
            setNextModal(true);
        } catch (error) {
            console.log(error);
            message.error("Có lỗi xảy ra khi xử lý");
        }
        setLoading(false);
    };
    
    const handleCancel = () => {
        setSelectedRows([]);
        setAllUserIds([]);
        setIsSelectAllPages(false);
        setNextModal(false);
        setIsModalOpen(false);
    };


    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={1400} 
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <Button 
                            key="submit" 
                            type="primary" 
                            loading={loading}
                            style={{ 
                                background: "var(--primary-color)", 
                                paddingRight: 48, 
                                paddingLeft: 48, 
                                fontSize: 18,
                                fontWeight: 600,
                                paddingTop: 18,
                                paddingBottom: 18,
                            }}
                            onClick={handleSave}
                        >
                            TIẾP TỤC ({isSelectAllPages ? allUserIds.length : selectedRows.length})
                        </Button>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">TẠO HỢP ĐỒNG ĐIỆN TỬ</p>
                </div>
                <div className="mt-4">
                    <FilterBar
                        setMap={setFilterMap}
                        onChange={onFilterChange}
                        templates={filterTemplates}
                        map={filterMap}
                    />
                </div>
                <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
                    <DataTable<ContractModel>
                        columns={[
                            { dataIndex: "code", name: "Mã KH" },
                            { dataIndex: "name", name: "Tên KH" },
                            { dataIndex: "phone", name: "Số điện thoại" },
                            { dataIndex: "email", name: "Email" },
                            { dataIndex: "address", name: "Địa chỉ" },
                        ]}
                        repository={contractRepo}
                        filterMap={filterMap}
                        onSelect={onRowSelect}
                        onPagination={onPagination}
                    />
                </div>
                {isSelectAllPages && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-yellow-800 text-sm">
                            <strong>⚠️ Chú ý:</strong> Đang chọn tất cả khách hàng từ tất cả các trang ({allUserIds.length} khách hàng)
                        </p>
                    </div>
                )}
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
            {isNextModal && (
                <MakeMultiElectronicContractModal 
                    isModalOpen={isNextModal} 
                    setIsModalOpen={setNextModal} 
                    data={selectedRows} 
                />
            )}
        </ConfigProvider>
    )
}
